import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditGoogleClassroom } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { GoogleClassroomClassSetting } from "@shared/models/card-stack"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../action-doc-builder"
import { partial } from "lodash"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  googleCardId: string
  classSetting: GoogleClassroomClassSetting
  className: string
  dateString: string
}

export default function prepareGCUnscheduleClass(payload: Payload): PreparedAction {
  return {
    name: "LESSON_CARD_GC_UNSCHEDULE_CLASS",
    payload: payload,
    patches: patches(payload),
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  let removeOld = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.googleCardId,
  })
    .pull("embedded", "attributes.classSettings", {
      googleClassId: payload.classSetting.googleClassId,
    })
    .inc("embedded", "attributes._revision", 1).patch

  let addNew = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.googleCardId,
  })
    .pull("embedded", "attributes.enabledGoogleClassIds", payload.classSetting.googleClassId)
    .push("embedded", "attributes.classSettings", payload.classSetting)
    .inc("embedded", "attributes._revision", 1).patch

  return [removeOld, addNew]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  let models = defaultLessonModels(payload, findFn, userId)
  let newEvent: LessonEditGoogleClassroom = {
    ...createBlankEvent(),
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: "google_classroom",

    adverbial_verb: "editing",
    adverbial_object_type: "card",
    adverbial_object_specifier: "gc_unschedule",
    adverbial_object_id: payload.googleCardId,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
  }

  return {
    title: "Scheduled a Google Classroom Class",
    titleForAnalytics: "Lesson Card - GC Post - Unschedule",
    description: `Unscheduled "${payload.className}" from posting on "${payload.dateString}"`,
    context: defaultLessonContext(payload, models),
    activity: defaultLessonActivity(payload, models, "deleted"),
    newAnalyticsEvents: [newEvent],
  }
}
